<template>
  <v-dialog v-model="internalDialog" max-width="1100px" scrollable persistent transition="dialog-bottom-transition">
    <v-card class="rounded-lg overflow-hidden bg-white">
      <!-- HEADER -->
      <v-card-title class="d-flex justify-space-between align-center py-4 px-6 error darken-2 white--text">
        <div class="d-flex align-center">
          <v-avatar color="white" size="40" class="me-3">
            <v-icon color="error darken-2" size="24">mdi-close-octagon-outline</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-black white--text mb-0">تقرير المبالغ المستردة للمحفظة</div>
            <div class="text-caption white--text text--lighten-3 opacity-90">
              حصر دقيق لكافة الأموال المستردة للعملاء نتيجة إغلاق أو إلغاء الحجوزات والرحلات
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <v-btn
            color="white"
            light
            class="font-weight-bold elevation-2 me-3 text-none error--text text--darken-3"
            :loading="loadingCsv"
            @click="exportCsv"
          >
            <v-icon left color="error darken-3">mdi-file-delimited</v-icon>
            تصدير CSV
          </v-btn>

          <v-btn icon dark @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- BODY -->
      <v-card-text class="pa-5 bg-grey-lighten-5">
        <v-alert v-if="errorMessage" type="error" dense class="mb-4 font-weight-bold d-flex align-center justify-space-between">
          <div>{{ errorMessage }}</div>
          <v-btn x-small color="white" text class="font-weight-bold ms-3" @click="fetchReport">إعادة المحاولة</v-btn>
        </v-alert>

        <!-- 1. FILTERS BAR -->
        <v-card outlined class="pa-4 mb-5 rounded-lg border-grey bg-white elevation-1">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">الفترة الزمنية:</div>
              <v-select
                v-model="selectedPeriod"
                :items="periodOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="onPeriodChange"
              ></v-select>
            </v-col>

            <v-col v-if="selectedPeriod === 'custom'" cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">من تاريخ:</div>
              <v-text-field
                v-model="dateFrom"
                type="date"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-text-field>
            </v-col>

            <v-col v-if="selectedPeriod === 'custom'" cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">إلى تاريخ:</div>
              <v-text-field
                v-model="dateTo"
                type="date"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">بحث برقم الحجز / اسم العميل:</div>
              <v-text-field
                v-model="searchQuery"
                placeholder="ابحث..."
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="bg-white"
                @keyup.enter="fetchReport"
                @click:clear="onClearSearch"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card>

        <!-- 2. KPI SUMMARY CARDS -->
        <v-row dense class="mb-5">
          <v-col cols="12" sm="6">
            <v-card outlined class="pa-4 rounded-lg border-error bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إجمالي المبالغ المستردة</div>
              <div class="text-h5 font-weight-black error--text text--darken-2 mt-1">
                {{ summary.total_refunded_amount || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">أموال مستردة فعلياً إلى محافظ العملاء</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6">
            <v-card outlined class="pa-4 rounded-lg border-warning bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">عدد عمليات الاسترداد</div>
              <div class="text-h5 font-weight-black warning--text text--darken-3 mt-1">
                {{ summary.refunds_count || 0 }} <span class="text-caption font-weight-bold">عملية</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">حجوزات ملغاة ذات رد مالي مثبت</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- 3. REFUNDS TABLE -->
        <v-card outlined class="rounded-lg border-grey bg-white elevation-1">
          <v-card-title class="pa-3 grey lighten-4 border-bottom d-flex align-center justify-space-between">
            <span class="text-subtitle-1 font-weight-bold text-dark-primary">
              <v-icon size="20" color="error" class="me-1">mdi-table</v-icon>
              سجل الحركات المالية المستردة ({{ table.total }} عملية)
            </span>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="table.data"
            :loading="loading"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.ticket_number="{ item }">
              <span class="font-weight-bold primary--text">{{ item.ticket_number }}</span>
            </template>

            <template v-slot:item.original_paid_amount="{ item }">
              <span class="font-weight-bold text-dark-primary">{{ item.original_paid_amount }} EGP</span>
            </template>

            <template v-slot:item.refunded_amount="{ item }">
              <span class="font-weight-black error--text text--darken-2">{{ item.refunded_amount }} EGP</span>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip x-small color="success" class="white--text font-weight-bold">{{ item.status }}</v-chip>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-6 text-dark-secondary">لا توجد عمليات استرداد في الفترة المحددة</div>
            </template>
          </v-data-table>

          <!-- PAGINATION -->
          <div class="pa-3 border-top d-flex justify-space-between align-center flex-wrap gap-2">
            <div class="text-caption text-dark-secondary font-weight-bold">
              عرض الصفحة {{ table.page }} من {{ table.last_page }} (إجمالي {{ table.total }} عملية)
            </div>
            <v-pagination
              v-if="table.last_page > 1"
              v-model="table.page"
              :length="table.last_page"
              total-visible="7"
              dense
              @input="fetchReport"
            ></v-pagination>
          </div>
        </v-card>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 bg-white d-flex justify-space-between align-center">
        <div class="caption font-weight-bold text-dark-secondary">
          BEEPGO Reporting Center — تقرير المبالغ المستردة
        </div>
        <v-btn color="grey darken-2" text class="font-weight-bold" @click="closeModal">إغلاق</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'RefundsModal',
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      internalDialog: false,
      loading: false,
      loadingCsv: false,
      errorMessage: null,

      selectedPeriod: 'month',
      dateFrom: null,
      dateTo: null,
      searchQuery: '',

      periodOptions: [
        { text: 'اليوم (Today)', value: 'today' },
        { text: 'هذا الأسبوع (This Week)', value: 'week' },
        { text: 'هذا الشهر (This Month)', value: 'month' },
        { text: 'هذه السنة (This Year)', value: 'year' },
        { text: 'آخر 7 أيام', value: 'last_7_days' },
        { text: 'آخر 30 يوم', value: 'last_30_days' },
        { text: 'فترة مخصصة (Custom)', value: 'custom' },
        { text: 'الكل (All Time)', value: 'all' },
      ],

      summary: {
        total_refunded_amount: '0.00',
        refunds_count: 0,
      },

      table: {
        data: [],
        total: 0,
        page: 1,
        per_page: 15,
        last_page: 1,
      },

      headers: [
        { text: 'رقم الحركة', value: 'id' },
        { text: 'رقم التذكرة', value: 'ticket_number' },
        { text: 'اسم العميل', value: 'customer_name' },
        { text: 'رقم الهاتف', value: 'customer_phone' },
        { text: 'المسار', value: 'route_name' },
        { text: 'تاريخ الإلغاء', value: 'cancellation_date' },
        { text: 'السبب', value: 'reason' },
        { text: 'المبلغ المسترد', value: 'refunded_amount' },
        { text: 'طريقة الرد', value: 'refund_method' },
        { text: 'الحالة', value: 'status' },
      ],
    };
  },
  watch: {
    value(val) {
      this.internalDialog = val;
      if (val) {
        this.fetchReport();
      }
    },
    internalDialog(val) {
      this.$emit('input', val);
    },
  },
  methods: {
    onPeriodChange() {
      this.table.page = 1;
      this.fetchReport();
    },
    onClearSearch() {
      this.searchQuery = '';
      this.table.page = 1;
      this.fetchReport();
    },
    fetchReport() {
      this.loading = true;
      this.errorMessage = null;

      const params = {
        period: this.selectedPeriod,
        search: this.searchQuery,
        page: this.table.page,
        per_page: this.table.per_page,
      };
      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get('/reports/financial/refunds', { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const d = res.data.data;
            this.summary = d.summary || this.summary;
            this.table = d.table || this.table;
          } else {
            this.errorMessage = res.data.message || 'خطأ في جلب التقرير';
          }
        })
        .catch((err) => {
          this.errorMessage = 'تعذر الاتصال بالخادم لجلب تقرير المستردات';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportCsv() {
      this.loadingCsv = true;
      const params = {
        period: this.selectedPeriod,
        search: this.searchQuery,
      };
      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get('/reports/financial/refunds/export-csv', {
          params,
          responseType: 'blob',
        })
        .then((res) => {
          const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `refund-report-${moment().format('YYYY-MM-DD')}.csv`;
          link.click();
          window.URL.revokeObjectURL(link.href);
        })
        .catch((err) => {
          console.error('Error exporting CSV:', err);
        })
        .finally(() => {
          this.loadingCsv = false;
        });
    },
    closeModal() {
      this.internalDialog = false;
    },
  },
};
</script>
